import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Actions, ofType, createEffect } from '@ngrx/effects';
import { from, of } from 'rxjs';
import { map, switchMap, catchError, withLatestFrom, mapTo, first } from 'rxjs/operators';
import { SystemPowerState, PowersService, Inverter } from 'ngx-core-services';

import {
  AppState,
  SystemsState,
  getSystemDefinitionDeviceIdsForTelemetries,
  getSystemDefinitionConfiguration,
  requestSystemPowerState,
  selectedSystemMetadata,
  setSystemPowerStateError,
  updateSystemPowerState
} from '..';
import { BuildPowerFlow, getSnapshotPowerAggregate } from './effects.utils';

@Injectable()
export class FlowChartEffects {

  requestSystemPowerState$ = createEffect(() =>
    this.actions$.pipe(
      ofType(requestSystemPowerState),
      switchMap((action) =>
        this.store.select(selectedSystemMetadata).pipe(
          first(),
          mapTo(action),
        ),
      ),
      withLatestFrom(this.store),
      switchMap(([action, { systemState }]) => {
        return from(this.getSystemPowerState(action.systemId, action.forceRefresh, systemState)).pipe(
          map((systemPowerState: SystemPowerState) => updateSystemPowerState({ systemId: action.systemId, instanceId: action.instanceId, systemPowerState })),
          catchError(_ => of(setSystemPowerStateError({ systemId: action.systemId, instanceId: action.instanceId, error: 'Failed to fetch system power state' })))
        );
      })
    )
  );

  constructor(
    private actions$: Actions,
    private store: Store<AppState>,
    private readonly powersService: PowersService,
  ) {}

  async getSystemPowerState(systemId: string, forceRefresh: boolean, state: SystemsState): Promise<SystemPowerState> {
    const system = state.systemList[systemId];
    if (!system) {
      throw new Error(`System with id ${systemId} not found`);
    }

    const systemConfiguration = getSystemDefinitionConfiguration(system);

    const serials = getSystemDefinitionDeviceIdsForTelemetries(system, { onlyInstalled: true });

    const powers: { [key: string]: Partial<Inverter>[] } = await this.powersService.fetchPowersState(serials, systemConfiguration.monitoringType, forceRefresh);

    const powersAsObject: { [key: string]: Partial<Inverter> } = Object.keys(powers).reduce((acc: { [key: string]: Partial<Inverter> }, key: string) => {
      if (powers[key] && powers[key][0]) {
          acc[key] = powers[key][0];
      }
      return acc;
    }, {});

    if( systemConfiguration.monitoringType === 'Solarman') {
      return BuildPowerFlow(getSnapshotPowerAggregate(powersAsObject, systemConfiguration.structure, !!systemConfiguration.inverterMeterSensor));
    }

    const connextKey = Object.keys(powersAsObject).length > 0 ? Object.keys(powersAsObject)[0] : '';
    const connextAggregate = connextKey ? powersAsObject[connextKey] : {};
    return BuildPowerFlow(connextAggregate);

  }
}
